import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, CreditCard, BookOpen, Zap, Star } from 'lucide-react';
import { CTAButton } from './Shared';
import { CandleFlame, OpenJournal, SacredCircle, FamilyTree } from './SacredIcons';
const heroImage = {
  src: 'https://cdn.magicpatterns.com/uploads/qkLLfS1fnRvNbMYKHciLLB/Mockup_Kit_terapeuta_pro.png',
  alt: 'Kit Terapeuta PRO™ — Sistema de Organización y Crecimiento Terapéutico'
};
export const Hero = () => {
  return <div className="relative overflow-hidden bg-transparent pt-2 pb-6 md:pt-20 md:pb-12" data-id="element-128">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-violetLight/10 rounded-full blur-[140px] -z-10 pointer-events-none" data-id="element-129"></div>

      {/* Floating Sacred decor (desktop only) */}
      <CandleFlame className="hidden md:block absolute top-32 left-[6%] w-8 h-12 opacity-30 animate-float-soft -z-10" style={{
      transform: 'rotate(5deg)'
    }} data-id="element-130" />
      <SacredCircle className="hidden md:block absolute top-48 right-[8%] w-24 h-24 opacity-20 animate-float-soft -z-10" style={{
      transform: 'rotate(-15deg)',
      animationDelay: '1.2s'
    }} data-id="element-131" />
      <OpenJournal className="hidden md:block absolute bottom-40 left-[4%] w-20 h-16 opacity-30 animate-float-soft -z-10" style={{
      animationDelay: '2s'
    }} data-id="element-132" />
      <FamilyTree className="hidden md:block absolute bottom-32 right-[5%] w-12 h-12 opacity-40 animate-float-soft -z-10" style={{
      transform: 'rotate(15deg)',
      animationDelay: '0.6s'
    }} data-id="element-133" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-134">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="bg-transparent" data-id="element-135">
          {/* Badge */}
          <div className="inline-flex items-center justify-center bg-white/80 backdrop-blur-sm border border-brand-gold/30 rounded-full gap-1.5 shadow-sm px-2.5 py-0.5 md:px-3 md:py-1 mb-1.5 md:mb-6" data-id="element-136">
            <span className="text-[11px] md:text-sm" data-id="element-137">🌿</span>
            <span className="font-serif font-semibold tracking-wider text-[10px] md:text-sm text-brand-violetDark" data-id="element-138">
              LUMEN AKASHA™
            </span>
          </div>

          {/* Headline */}
          <div className="relative inline-block mb-1.5 md:mb-5 max-w-4xl mx-auto" data-id="element-139">
            <h1 className="text-[28px] sm:text-5xl md:text-6xl lg:text-7xl font-[700] leading-[1.18] tracking-tight" style={{
            fontFamily: "'Cinzel', serif"
          }} data-id="element-140">
              <span className="headline-shine sm:text-5xl md:text-6xl lg:text-7xl text-[28px] font-[700] font-[Georgia,_serif]" data-id="element-141">
                NO NECESITÁS OTRO CURSO.
              </span>
              <br data-id="element-142" />
              <span className="highlight-marker text-[26px] md:text-5xl font-[700] font-[Georgia,_serif]" data-id="element-143">
                Necesitas ORDEN
              </span>
            </h1>
          </div>

          {/* Hero Image */}
          <div className="relative z-10 w-full max-w-[300px] sm:max-w-md md:max-w-2xl lg:max-w-3xl mx-auto mb-1 md:mb-3 flex justify-center" data-id="element-144">
            <motion.img src={heroImage.src} alt={heroImage.alt} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className="w-full h-auto max-h-[46vh] md:max-h-none object-contain drop-shadow-2xl" draggable={false} data-id="element-145" />
          </div>

          {/* Short value prop */}
          <div className="max-w-2xl mx-auto mb-1.5 md:mb-4 px-2 space-y-0 md:space-y-1.5" data-id="element-146">
            <p className="text-[15px] md:text-2xl font-bold text-brand-violet font-serif leading-snug" data-id="element-147">
              🌟 KIT TERAPEUTA PRO™
            </p>
            <p className="hidden md:block text-[12px] md:text-lg text-brand-text/80 font-sans leading-snug" data-id="element-148">
              ✨ Sistema completo de organización y crecimiento terapéutico.
            </p>
          </div>

          {/* Chips (desktop only — reclaim vertical space on mobile) */}
          <div className="hidden md:flex flex-wrap items-center justify-center gap-1.5 md:gap-3 mb-2 md:mb-6 text-xs md:text-sm font-medium px-2" data-id="element-149">
            <div className="inline-flex items-center bg-white rounded-full border border-brand-violet/20 shadow-sm text-[11px] md:text-sm px-3 py-1.5 md:px-4 md:py-2 font-semibold text-brand-violetDark" data-id="element-150">
              <BookOpen className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-violet shrink-0" data-id="element-151" />
              100% PDF
            </div>
            <div className="inline-flex items-center bg-white rounded-full border border-brand-violet/20 shadow-sm text-[11px] md:text-sm px-3 py-1.5 md:px-4 md:py-2 font-semibold text-brand-violetDark" data-id="element-152">
              <Zap className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-violet shrink-0" data-id="element-153" />
              Acceso en 5 min
            </div>
            <div className="inline-flex items-center bg-white rounded-full border border-brand-violet/20 shadow-sm text-[11px] md:text-sm px-3 py-1.5 md:px-4 md:py-2 font-semibold text-brand-violetDark" data-id="element-154">
              <span className="mr-1 md:mr-1.5 text-brand-violet shrink-0" data-id="element-155">
                🌿
              </span>
              Para toda terapia
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col items-center space-y-1.5 md:space-y-6 mb-3 md:mb-12 scroll-mt-24" data-id="element-156">
            <CTAButton variant="purple" className="text-[15px] md:text-2xl px-6 md:px-12 py-3.5 md:py-6 cta-shimmer relative overflow-hidden font-bold" data-id="element-157">
              <span className="relative z-10" data-id="element-158">🌿 → QUIERO MI KIT AHORA</span>
            </CTAButton>

            {/* Compact mobile trust row */}
            <div className="flex md:hidden flex-wrap justify-center items-center gap-x-2.5 gap-y-1 text-[10px] text-brand-muted" data-id="element-159">
              <div className="flex items-center" data-id="element-160">
                <Shield className="w-3 h-3 mr-1 text-brand-goldDark" data-id="element-161" />
                Garantía 7 días
              </div>
              <span className="text-brand-gold/50" data-id="element-162">·</span>
              <div className="flex items-center" data-id="element-163">📥 Descarga inmediata</div>
              <span className="text-brand-gold/50" data-id="element-164">·</span>
              <div className="flex items-center" data-id="element-165">
                <Lock className="w-3 h-3 mr-1 text-brand-goldDark" data-id="element-166" /> Pago
                seguro
              </div>
            </div>
          </div>

          {/* Extra detail below the fold */}
          <div className="max-w-3xl mx-auto mb-6 md:mb-8 mt-2 md:mt-0 px-2" data-id="element-167">
            <p className="text-[13px] md:text-lg text-brand-text/80 font-sans leading-snug" data-id="element-168">
              📚 Agenda terapéutica · 📝 Fichas clínicas · 🧭 Seguimiento · 📊
              Planificación · 📓 Diario del terapeuta
            </p>
          </div>

          {/* Full trust badges (desktop only) */}
          <div className="hidden md:flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-xs md:text-sm text-brand-muted" data-id="element-169">
            <div className="flex items-center" data-id="element-170">
              <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-goldDark" data-id="element-171" />{' '}
              Garantía 7 días
            </div>
            <div className="flex items-center" data-id="element-172">📥 Descarga inmediata</div>
            <div className="flex items-center" data-id="element-173">
              🖨️ Listo para imprimir o tablet
            </div>
            <div className="flex items-center" data-id="element-174">
              <Lock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-goldDark" data-id="element-175" />{' '}
              Pago seguro
            </div>
            <div className="flex items-center" data-id="element-176">
              <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-goldDark" data-id="element-177" />{' '}
              Mercado Pago
            </div>
          </div>
        </motion.div>
      </div>

      {/* Social Proof Bar */}
      <div className="mt-8 md:mt-12 border-y border-brand-violet/15 bg-white/60 py-3 md:py-4 backdrop-blur-sm" data-id="element-178">
        <div className="md:hidden flex flex-wrap justify-center gap-x-4 gap-y-1 px-4 text-[11px]" data-id="element-179">
          <div className="flex items-center" data-id="element-180">
            <span className="font-bold text-brand-violet mr-1" data-id="element-181">+500</span>{' '}
            terapeutas
          </div>
          <div className="flex items-center" data-id="element-182">
            <span className="font-bold text-brand-violet mr-1" data-id="element-183">1</span> Planner
          </div>
          <div className="flex items-center" data-id="element-184">
            <span className="font-bold text-brand-violet mr-1" data-id="element-185">4</span> Bonos
          </div>
          <div className="flex items-center" data-id="element-186">
            <Star className="w-3 h-3 text-brand-goldDark fill-brand-goldDark mr-0.5" data-id="element-187" />{' '}
            4.9/5
          </div>
        </div>

        <div className="hidden md:flex flex-row items-center gap-6 justify-center whitespace-nowrap px-4 text-sm" data-id="element-188">
          <div className="flex items-center" data-id="element-189">
            <span className="font-bold text-brand-violet mr-2" data-id="element-190">+500</span>{' '}
            terapeutas organizados
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-gold/50 shrink-0" data-id="element-191"></div>
          <div className="flex items-center" data-id="element-192">
            <span className="font-bold text-brand-violet mr-2" data-id="element-193">1</span> Planner
            incluido
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-gold/50 shrink-0" data-id="element-194"></div>
          <div className="flex items-center" data-id="element-195">
            <span className="font-bold text-brand-violet mr-2" data-id="element-196">4</span> Bonos
            incluidos
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-gold/50 shrink-0" data-id="element-197"></div>
          <div className="flex items-center" data-id="element-198">
            <Star className="w-4 h-4 text-brand-goldDark fill-brand-goldDark mr-1" data-id="element-199" />{' '}
            4.9/5 calificación
          </div>
        </div>
      </div>
    </div>;
};